<template>
  <span
    v-if="inlineSvg"
    :class="cn('tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-leading-none', props.class)"
    :style="sizeStyle"
    :aria-label="ariaLabel"
    :role="ariaLabel ? 'img' : undefined"
    :aria-hidden="ariaLabel ? undefined : true"
    v-html="inlineSvg"
  />
</template>

<script setup lang="ts">
import { computed, type HTMLAttributes } from "vue"
import { cn } from "@/components/ui/utils"

const props = withDefaults(
  defineProps<{
    /** Kebab-case name of an icon inlined from `src/assets/icons/`. */
    name: string
    /** Pixel size; defaults to 16 to match the shadcn `[&_svg]:tw-size-4` rule. */
    size?: number
    /** Accessible label. Pass when the icon conveys meaning on its own. */
    ariaLabel?: string
    /** Extra Tailwind classes (color, hover, etc.). */
    class?: HTMLAttributes["class"]
  }>(),
  {
    size: 16,
    ariaLabel: undefined,
    class: undefined
  }
)

// Inline SVGs at build time. `currentColor` rewrite happens once per file at
// module load — not per mount.
const rawSvgs = import.meta.glob<string>("@/assets/icons/*.svg", {
  query: "?raw",
  import: "default",
  eager: true
})

const svgsByName: Record<string, string> = Object.fromEntries(
  Object.entries(rawSvgs).map(([path, raw]) => {
    const name = path
      .split("/")
      .pop()!
      .replace(/\.svg$/, "")
    const normalized = raw
      .replace(/stroke="var\(--stroke-0,[^)]*\)"/g, 'stroke="currentColor"')
      .replace(/fill="var\(--fill-0,[^)]*\)"/g, 'fill="currentColor"')
    return [name, normalized]
  })
)

const inlineSvg = computed<string | null>(() => svgsByName[props.name] ?? null)

const sizeStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`
}))
</script>
